import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/atoms/Accordion'

import timeFormatter from '@frontend/utils/timeFormatter'
import { scopedTranslation } from '@utils/I18n'

import { ChangeBreakdown, changeCount } from '../roster_board/ChangeBreakdown'
import { useEventTimeZone } from '../author_shifts/ShiftAuthoringContext'
import { isPendingRemoval } from '../shifts/publishState'
import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('components.publish_roster')

function dayOf(shift: ShiftWithSlots, zone?: string | null) {
  return timeFormatter.fromISO(shift.startTime, zone).toLongDate()
}

function stateLine(shift: ShiftWithSlots): string | null {
  if (isPendingRemoval(shift)) return t('shift_state_cancelled')

  if (!shift.changeBreakdown) {
    return shift.shiftSlots.assignedCount > 0 ? t('shift_state_first_publish') : t('shift_state_new')
  }

  return null
}

function ShiftBlock({ shift }: { shift: ShiftWithSlots }) {
  const line = stateLine(shift)
  const timeZone = useEventTimeZone()

  return (
    <div className="tw:flex tw:flex-col tw:gap-1 tw:border-t tw:border-gray-100 tw:py-2 tw:first:border-t-0">
      <div className="tw:flex tw:items-baseline tw:gap-2">
        <span className="tw:w-14 tw:shrink-0 tw:text-xs tw:tabular-nums tw:text-gray-500">
          {timeFormatter.fromISO(shift.startTime, timeZone).toHoursMinutes()}
        </span>
        <span className="tw:text-xs tw:font-medium tw:text-gray-900">{shift.roleName}</span>
      </div>

      <div className="tw:pl-16">
        {shift.changeBreakdown && changeCount(shift.changeBreakdown) > 0 && (
          <ChangeBreakdown breakdown={shift.changeBreakdown} showTitle={false} />
        )}

        {line && <p className="tw:text-xs tw:text-gray-500">{line}</p>}
      </div>
    </div>
  )
}

export function PublishChangeList({ shifts }: { shifts: ShiftWithSlots[] }) {
  const timeZone = useEventTimeZone()

  if (shifts.length === 0) return null

  const days = [...new Set(shifts.map((shift) => dayOf(shift, timeZone)))]
  const shiftSummary = t('changes_summary', { count: shifts.length })
  const summary =
    days.length > 1 ? t('changes_summary_across_days', { shifts: shiftSummary, count: days.length }) : shiftSummary

  return (
    <Accordion type="single" collapsible className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-200">
      <AccordionItem value="changes" className="tw:border-b-0">
        <AccordionTrigger className="tw:gap-2.5 tw:px-3 tw:py-2.5 tw:data-[state=closed]:hover:bg-gray-50 tw:hover:no-underline">
          <span className="tw:text-sm tw:font-medium tw:text-gray-900">{t('changes_title')}</span>
          <span className="tw:ml-auto tw:text-xs tw:tabular-nums tw:text-gray-500">{summary}</span>
        </AccordionTrigger>
        <AccordionContent className="tw:max-h-56 tw:overflow-y-auto tw:border-t tw:border-gray-200 tw:px-3 tw:pb-3 tw:pt-1">
          {days.map((day) => (
            <div key={day} className="tw:flex tw:flex-col">
              <p className="tw:pb-1 tw:pt-2 tw:text-sm tw:font-medium tw:text-gray-700">{day}</p>
              {shifts
                .filter((shift) => dayOf(shift, timeZone) === day)
                .map((shift) => (
                  <ShiftBlock key={shift.id} shift={shift} />
                ))}
            </div>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
